import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { flushLogs, createLogger } from '../../../../../utilities/log';
import { stripePaymentMethodsImage, swishIconImage } from '../../../../assets';
import { Button, Loader } from '../../../../../components';
import Alert from 'react-bootstrap/Alert';
import { t } from '../../../../../i18n';
import { loadCoursesCheckoutInformation } from '../../redux/actions';
import { postCourseCheckoutPromocode } from '../../../../../backend-api';
import UrlTo from '../../../../../url-to';
import PromoCodeInput from '../promocode-input';
import CheckoutContext from '../../checkout-context';
import StripeMimicButton from './stripe-mimic-button';
import SwishPaymentForm from './swish-payment-form';
import './styles.scss';

const logger = createLogger('Courses/Checkout');

const CheckoutForm = ({ courseId, email, name, hidePromocodeInput }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [promocode, setPromocode] = useState('');
  const [promocodeError, setPromocodeError] = useState('');
  const [isPromocodeLoading, setIsPromocodeLoading] = useState(false);

  const { swish, setSwish, isCoursePayment } = useContext(CheckoutContext);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    logger.trace('Before confirmPayment', { email, name, isCoursePayment });
    flushLogs();

    const returnUrl = isCoursePayment
      ? UrlTo.coursesStripeCheckoutResult(courseId, email, name)
      : UrlTo.coursesGiftCardStripeCheckoutResult({ email, name });
    // Should be absolute URL because sometimes stripe redirects to another site
    const absoluteReturnUrl = `${window.location.protocol}//${window.location.host}${returnUrl}`;
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: absoluteReturnUrl,
        payment_method_data: {
          billing_details: {
            email,
            name,
          },
        },
      },
    });

    logger.trace('After confirmPayment', { email, name, isCoursePayment, error });
    flushLogs();

    // Immediate error
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message);
    } else {
      setMessage(t('DIGITAL_COURSES.CHECKOUT.ERROR.UNKNOWN_ERROR'));
    }

    setIsLoading(false);
  };

  const hideEmailAndFullName = {
    // because we use our own form data for this
    fields: {
      billingDetails: {
        email: 'never',
        name: 'never',
      },
    },
  };

  const handleApplyPromocode = async () => {
    try {
      setPromocodeError('');
      setIsPromocodeLoading(true);
      await postCourseCheckoutPromocode({
        courseId,
        email,
        promocode,
      });
      const currency = swish ? 'sek' : null;
      dispatch(loadCoursesCheckoutInformation(courseId, { email, currency }));
    } catch (ex) {
      const serverError = ex.response?.data.error;
      const errorMessage = serverError ? t(`DIGITAL_COURSES.CHECKOUT.ERROR.${serverError}`) : t('SERVER_ERROR');
      setPromocodeError(errorMessage);
      logger.info('Promocode activation error', { courseId, email, promocode });
    } finally {
      setIsPromocodeLoading(false);
    }
  };

  return (
    <form className="courses-checkout-form">
      {message && (
        <Alert variant="danger" className="courses-checkout-form__error">
          {message}
        </Alert>
      )}
      {!hidePromocodeInput && (
        <div className="courses-checkout-form__promocode">
          <PromoCodeInput
            value={promocode}
            error={promocodeError}
            isLoading={isPromocodeLoading}
            onChange={e => setPromocode(e.target.value.toUpperCase())}
            onBlur={() => setPromocode(oldText => oldText.trim())}
            onApplyPromocode={handleApplyPromocode}
          />
        </div>
      )}
      <div className="courses-checkout-form__payment-types">
        <StripeMimicButton
          text="Swish"
          iconSrc={swishIconImage}
          isSelected={swish}
          onSelected={() => setSwish(!swish)}
        />
        <StripeMimicButton
          text={t('DIGITAL_COURSES.CHECKOUT.STRIPE_PAYMENT_METHODS')}
          iconSrc={stripePaymentMethodsImage}
          isSelected={!swish}
          onSelected={() => setSwish(!swish)}
        />
      </div>
      {!swish && (
        <>
          <PaymentElement options={hideEmailAndFullName} className="courses-checkout-form__payment-form" />
          <Button
            text={t('DIGITAL_COURSES.CHECKOUT.PAY_NOW')}
            disabled={isLoading || !stripe || !elements}
            type="green"
            onClick={handleSubmit}
            className="courses-checkout-form__submit-button"
          />
        </>
      )}
      {swish && <SwishPaymentForm courseId={courseId} email={email} name={name} />}
      {isLoading && <Loader />}
    </form>
  );
};

export default CheckoutForm;
